import {FC} from 'react'

import {Text, TextVariant} from '@/components/common/content/Text'
import {JurisdictionTooltip} from '@/components/trademark-ownership-transfer/form/jurisdictions/tooltip/JurisdictionTooltip'
import {TrademarkOwnershipTransferPrefillSource} from '@/components/trademark-ownership-transfer/form/types'
import {getPricingContent} from '@/content/pricing'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {Jurisdiction} from '@/utils/countries/constants'
import {calculateTmOwnershipTransferCountryPrice} from '@/utils/pricing/tm-ownership-transfer/calculations'
import {routeTo} from '@/utils/router'
import {tmOwnershipTransferJurisdictions} from '@/utils/trademark-ownership-transfer/constants'

import {PricingCalculatorTable} from '../calculator/PricingCalculatorTable'
import {
  AssignmentAgreementProvider,
  useAssignmentAgreement,
} from './context/AssignmentAgreementContext'
import {PricingAssignmentAgreementRadioButtons} from './PricingAssignmentAgreementRadioButtons'
import {PricingTrademarkOwnershipTransferEstimationCard} from './PricingTrademarkOwnershipTransferEstimationCard'

const PricingTrademarkOwnershipTransferCalculator: FC = () => {
  const {t} = useTranslation()
  const {subtitle, calculator} = getPricingContent().trademarkOwnershipTransfer
  const {hasAssignmentAgreement} = useAssignmentAgreement()

  const getLinkHref = (
    selectedJurisdictions: Jurisdiction[],
    _classesByJurisdiction: Partial<Record<Jurisdiction, number>>,
    trademarksByJurisdiction: Partial<Record<Jurisdiction, number>>,
  ) =>
    routeTo.trademarkOwnershipTransfer.form({
      jurisdictions:
        selectedJurisdictions.map((jurisdiction) => jurisdiction.toLowerCase()).join(',') ||
        undefined,
      trademarksCount:
        selectedJurisdictions
          .map((jurisdiction) => trademarksByJurisdiction[jurisdiction] ?? 1)
          .join(',') || undefined,
      hasAssignmentAgreement,
      prefillSource: TrademarkOwnershipTransferPrefillSource.Pricing,
    })

  return (
    <>
      <PricingCalculatorTable
        subtitle={subtitle}
        startButtonText={calculator.startButtonText}
        continueButtonText={calculator.continueButtonText}
        getLinkHref={getLinkHref}
        trackedEventName="TrademarkOwnershipTransferButtonPricelist"
        EstimationCard={PricingTrademarkOwnershipTransferEstimationCard}
        allowedJurisdictions={tmOwnershipTransferJurisdictions}
        allowEuMemberSelection
        getJurisdictionPricing={(jurisdiction, currency) => ({
          price: calculateTmOwnershipTransferCountryPrice(jurisdiction, currency).total.vatBase,
          renderTooltip: () => <JurisdictionTooltip jurisdiction={jurisdiction} />,
        })}
        mobileChildren={
          <div className="wrapper">
            <Text variant={TextVariant.h5} element="h3">
              {t(calculator.assignmentAgreementQuestion)}
            </Text>
            <div className="radio-wrapper">
              <PricingAssignmentAgreementRadioButtons />
            </div>
          </div>
        }
      />

      <style jsx>{`
        .wrapper {
          margin: ${theme.spacing[6]} 0;
        }
        .radio-wrapper {
          margin-top: ${theme.spacing[3]};
        }
      `}</style>
    </>
  )
}

export const PricingTrademarkOwnershipTransferTable: FC = () => (
  <AssignmentAgreementProvider>
    <PricingTrademarkOwnershipTransferCalculator />
  </AssignmentAgreementProvider>
)
